import React from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, CalendarDays, Download } from "lucide-react";
import api from "@/lib/api";
import useFreshData from "@/lib/useFreshData";
import SEO, { SITE_URL, breadcrumbSchema } from "@/components/seo/SEO";

const formatDate = (iso) => {
    try {
        return new Intl.DateTimeFormat("es-ES", {
            day: "numeric",
            month: "long",
            year: "numeric",
        }).format(new Date(`${iso}T12:00:00`));
    } catch {
        return iso;
    }
};

export default function PressReleaseDetail() {
    const { slug } = useParams();
    const { data: item, loading } = useFreshData(
        async () => (await api.get(`/press/${slug}`)).data,
        { refreshMs: 0, deps: [slug] }
    );

    if (loading) {
        return (
            <div
                data-testid="press-loading"
                className="max-w-3xl mx-auto px-4 py-32 text-center text-[#4A5568]"
            >
                Cargando nota de prensa...
            </div>
        );
    }

    if (!item) {
        return (
            <div
                data-testid="press-not-found"
                className="max-w-3xl mx-auto px-4 py-32 text-center"
            >
                <h1 className="text-2xl font-semibold text-[#082050]">
                    Nota de prensa no encontrada
                </h1>
                <Link
                    to="/institucional"
                    className="mt-6 inline-flex items-center gap-2 text-sm text-[#082050] underline"
                >
                    <ArrowLeft size={14} /> Volver a Institucional
                </Link>
            </div>
        );
    }

    const images = item.images || [];
    const pdfUrl = `/api/press/${item.slug}/pdf`;

    return (
        <div data-testid="press-detail-page" className="bg-white">
            <SEO
                title={item.seo_title || item.title}
                description={item.seo_description || item.excerpt}
                path={`/institucional/actualidad/${item.slug}`}
                image={
                    images[0]
                        ? `${SITE_URL}${images[0]}`
                        : undefined
                }
                type="article"
                jsonLd={[
                    {
                        "@context": "https://schema.org",
                        "@type": "NewsArticle",
                        "headline": item.title,
                        "description": item.excerpt,
                        "datePublished": item.date,
                        "image": images.map((i) => `${SITE_URL}${i}`),
                        "author": {
                            "@type": "Organization",
                            "name": "ASATTA · Asociación de Alojamientos Turísticos de Tarifa",
                            "url": "https://asatta.org",
                        },
                    },
                    breadcrumbSchema([
                        { name: "Inicio", path: "/" },
                        { name: "Institucional", path: "/institucional" },
                        {
                            name: "Actualidad Institucional",
                            path: "/institucional#actualidad",
                        },
                        {
                            name: item.title,
                            path: `/institucional/actualidad/${item.slug}`,
                        },
                    ]),
                ]}
            />

            {/* Breadcrumb */}
            <nav
                aria-label="Breadcrumb"
                className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 text-xs text-[#718096]"
            >
                <ol className="flex items-center gap-2 flex-wrap">
                    <li>
                        <Link to="/" className="hover:text-[#082050]">Inicio</Link>
                    </li>
                    <li aria-hidden="true">/</li>
                    <li>
                        <Link to="/institucional" className="hover:text-[#082050]">
                            Institucional
                        </Link>
                    </li>
                    <li aria-hidden="true">/</li>
                    <li className="text-[#082050]">Actualidad Institucional</li>
                </ol>
            </nav>

            {/* Cabecera del artículo */}
            <header className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-10">
                <div className="flex items-center gap-3 text-xs uppercase tracking-widest text-[#082050]/70">
                    <span className="border border-[#082050]/20 px-3 py-1.5">
                        Nota de prensa
                    </span>
                    <span className="inline-flex items-center gap-1.5">
                        <CalendarDays size={13} />
                        {formatDate(item.date)}
                    </span>
                </div>
                <h1 className="mt-6 text-3xl sm:text-4xl lg:text-5xl font-semibold text-[#082050] tracking-tight leading-[1.1]">
                    {item.title}
                </h1>
                <p className="mt-6 text-lg sm:text-xl text-[#4A5568] leading-relaxed">
                    {item.lead}
                </p>
                <div className="mt-8">
                    <a
                        href={pdfUrl}
                        download
                        data-testid="btn-press-pdf"
                        className="inline-flex items-center gap-2 bg-[#082050] text-white px-6 py-3.5 text-sm font-medium hover:bg-[#082050]/90 transition-transform hover:-translate-y-0.5"
                    >
                        <Download size={16} />
                        Descargar Nota de Prensa (PDF)
                    </a>
                </div>
            </header>

            {/* Foto principal */}
            {images[0] && (
                <figure className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pb-14">
                    <img
                        src={images[0]}
                        alt={`Imagen de la nota de prensa: ${item.title}`}
                        className="w-full aspect-[16/9] object-cover"
                        loading="eager"
                    />
                    <figcaption className="mt-3 text-xs text-[#718096]">
                        Reunión institucional en la Cámara de Comercio del Campo
                        de Gibraltar · 10 de septiembre de 2026
                    </figcaption>
                </figure>
            )}

            {/* Cuerpo */}
            <article className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pb-10">
                {(item.sections || []).map((sec, i) => (
                    <section key={i} data-testid={`press-section-${i}`} className="mb-10">
                        {sec.heading && (
                            <h2 className="text-2xl sm:text-3xl font-semibold text-[#082050] tracking-tight mb-5">
                                {sec.heading}
                            </h2>
                        )}
                        <div className="space-y-5">
                            {sec.paragraphs.map((p, j) => (
                                <p
                                    key={j}
                                    className="text-base sm:text-lg text-[#4A5568] leading-relaxed"
                                >
                                    {p}
                                </p>
                            ))}
                        </div>
                    </section>
                ))}

                {/* Galería secundaria */}
                {images.length > 1 && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 my-12">
                        {images.slice(1).map((img, i) => (
                            <img
                                key={img}
                                src={img}
                                alt={`Imagen ${i + 2} de la reunión institucional`}
                                className="w-full aspect-[4/3] object-cover"
                                loading="lazy"
                            />
                        ))}
                    </div>
                )}

                {/* Cierre */}
                <div className="mt-14 border-t border-[#E2E4E9] pt-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                    <Link
                        to="/institucional"
                        data-testid="btn-press-back"
                        className="inline-flex items-center gap-2 text-sm text-[#082050] hover:gap-3 transition-all"
                    >
                        <ArrowLeft size={14} />
                        Volver a Institucional
                    </Link>
                    <a
                        href={pdfUrl}
                        download
                        data-testid="btn-press-pdf-bottom"
                        className="inline-flex items-center gap-2 border border-[#082050] text-[#082050] px-6 py-3 text-sm font-medium hover:bg-[#082050]/5"
                    >
                        <Download size={14} />
                        Nota de prensa en PDF
                        <ArrowUpRight size={14} />
                    </a>
                </div>
            </article>
        </div>
    );
}
